import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import type { DataNode, EventDataNode } from 'antd/es/tree';

import { useAuthorization } from '@/shared/hooks/useAuthorization';
import { ROUTES, TREE_DEPTH } from '@/shared/lib/constants';
import { calculateFqnDepth, getParentFqn, parseFqnToPathParts, removeCategoryPrefix } from '@/shared/lib/format/fqn';
import { tagKeys } from '@/shared/lib/query/queryKeys/tags';
import type { TreeNode } from '@/shared/lib/tree';
import { CategoryTree } from '@/shared/ui/CategoryTree';
import { ErrorFallback } from '@/shared/ui/ErrorBoundary';

import type { TagCategoryItem, TagDetailItem } from '@/entities/tags/domain';

import { useTagCategories, useTags } from '@/features/tags/model';

const NAVIGATION_DEPTH = 0;

const transformCategoryToTreeNode = (categories: TagCategoryItem[]): TreeNode[] => {
  if (categories.length === 0) return [];

  const treeNodes = categories.map((category) => {
    const categoryKey = category.name;
    const depth = calculateFqnDepth(categoryKey);

    const hasNoTerms = !category.termCount;
    const isMaxDepth = depth >= TREE_DEPTH.TAG;
    const isLeaf = hasNoTerms || isMaxDepth;

    return {
      key: categoryKey,
      title: category.displayName || category.name,
      count: category.termCount,
      isLeaf,
    };
  });
  return treeNodes;
};

const transformTagToTreeNode = (tags: TagDetailItem[]): TreeNode[] => {
  return tags.map((tag) => {
    const depth = calculateFqnDepth(tag.fullyQualifiedName);

    const hasNoUsage = !tag.usageCount;
    const isMaxDepth = depth >= TREE_DEPTH.TAG;
    const isLeaf = hasNoUsage || isMaxDepth;

    return {
      key: tag.fullyQualifiedName,
      title: tag.displayName || tag.name,
      count: tag.usageCount,
      isLeaf,
    };
  });
};

type TagCategoryTreeProps = {
  selectedTagFqn?: string;
  onAddClick?: () => void;
  refreshParentFqn?: string | null;
  onRefreshComplete?: () => void;
};

const Tree = ({ selectedTagFqn, onAddClick, refreshParentFqn, onRefreshComplete }: TagCategoryTreeProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tagCategory } = useParams<{ tagCategory?: string }>();
  const { hasAnyRole } = useAuthorization();

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [loadedChildren, setLoadedChildren] = useState<Map<string, TreeNode[]>>(new Map());
  const [pendingLoadKey, setPendingLoadKey] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const prevRefreshParentFqnRef = useRef<string | null>(null);

  const canManageCategories = hasAnyRole(['admin', 'manager']);

  const { data: categoryData, isLoading: isCategoryLoading } = useTagCategories({ limit: 3000 });
  const { data: tagData, isLoading: isTagLoading } = useTags({
    parent: pendingLoadKey ?? '',
  });

  const getChildrenToNode = useCallback(
    (node: TreeNode): TreeNode => {
      const cachedChildren = loadedChildren.get(node.key);

      if (!cachedChildren) return node;
      return {
        ...node,
        children: cachedChildren.map((child) => getChildrenToNode(child)),
      };
    },
    [loadedChildren]
  );

  const treeData = useMemo(() => {
    if (!categoryData) return [];
    const baseTreeData = transformCategoryToTreeNode(categoryData);
    return baseTreeData.map((node) => getChildrenToNode(node));
  }, [categoryData, getChildrenToNode]);

  const onCollapseAll = useCallback(() => {
    if (expandedKeys.length === 0) return;
    setExpandedKeys([]);
  }, [expandedKeys.length]);

  const onSelect = useCallback(
    (
      newSelectedKeys: React.Key[],
      info: {
        event: 'select';
        selected: boolean;
        node: EventDataNode<DataNode>;
        selectedNodes: DataNode[];
        nativeEvent: MouseEvent;
      }
    ) => {
      if (!info.selected || newSelectedKeys.length === 0) {
        setSelectedKeys([]);
        return;
      }

      const selectedKey = newSelectedKeys[0] as string;
      const depth = calculateFqnDepth(selectedKey);
      setSelectedKeys(newSelectedKeys);

      if (depth === NAVIGATION_DEPTH) {
        navigate(`${ROUTES.TAGS}/${selectedKey}`);
      } else {
        const category = parseFqnToPathParts(selectedKey)[0];
        const fqn = removeCategoryPrefix(selectedKey);
        navigate(`${ROUTES.TAGS}/${category}/${fqn}`);
      }

      if (depth >= TREE_DEPTH.TAG) return;
      if (!expandedKeys.includes(selectedKey)) {
        setExpandedKeys([...expandedKeys, selectedKey]);

        if (!loadedChildren.has(selectedKey) && !pendingLoadKey) {
          setPendingLoadKey(selectedKey);
        }
      }
    },
    [expandedKeys, loadedChildren, navigate, pendingLoadKey]
  );

  const onExpand = useCallback(
    (newExpandedKeys: React.Key[], info: { expanded: boolean; node: EventDataNode<DataNode> }) => {
      const filteredExpandedKeys = newExpandedKeys.filter((key) => {
        const depth = calculateFqnDepth(key as string);
        const isMaxDepth = depth >= TREE_DEPTH.TAG;
        return !isMaxDepth;
      });

      setExpandedKeys(filteredExpandedKeys);

      if (info.expanded && info.node && info.node.key) {
        const expandedKey = info.node.key as string;
        const depth = calculateFqnDepth(expandedKey);

        if (depth < TREE_DEPTH.TAG) {
          if (!loadedChildren.has(expandedKey)) {
            setPendingLoadKey(expandedKey);
          }
        }
      }
    },
    [loadedChildren]
  );

  const loadingKeys = isTagLoading && pendingLoadKey ? [pendingLoadKey] : [];

  useEffect(() => {
    if (!pendingLoadKey || !tagData?.data) return;

    const depth = calculateFqnDepth(pendingLoadKey);
    if (depth >= TREE_DEPTH.TAG) return;

    const children = transformTagToTreeNode(tagData.data);
    setLoadedChildren((prev) => {
      const newMap = new Map(prev);
      newMap.set(pendingLoadKey, children);
      return newMap;
    });

    setPendingLoadKey(null);
  }, [pendingLoadKey, tagData]);

  useEffect(() => {
    if (
      refreshParentFqn === null ||
      refreshParentFqn === undefined ||
      refreshParentFqn === prevRefreshParentFqnRef.current
    )
      return;

    prevRefreshParentFqnRef.current = refreshParentFqn ?? null;

    queryClient.invalidateQueries({ queryKey: tagKeys.categories() });

    if (refreshParentFqn === '') {
      setLoadedChildren(new Map());
      onRefreshComplete?.();
      return;
    }

    queryClient.removeQueries({ queryKey: tagKeys.tags(refreshParentFqn) });

    const parentFqn = getParentFqn(refreshParentFqn);
    if (parentFqn) {
      queryClient.removeQueries({ queryKey: tagKeys.tags(parentFqn) });

      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.delete(refreshParentFqn);
        newMap.delete(parentFqn);
        return newMap;
      });

      if (expandedKeys.includes(parentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(parentFqn);
      } else if (expandedKeys.includes(refreshParentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(refreshParentFqn);
      }
    } else {
      setLoadedChildren((prev) => {
        const newMap = new Map(prev);
        newMap.delete(refreshParentFqn);
        return newMap;
      });

      if (expandedKeys.includes(refreshParentFqn) && pendingLoadKey === null) {
        setPendingLoadKey(refreshParentFqn);
      }
    }

    prevRefreshParentFqnRef.current = null;
    onRefreshComplete?.();
  }, [refreshParentFqn, queryClient, expandedKeys, pendingLoadKey, onRefreshComplete]);

  useEffect(() => {
    if (pendingLoadKey !== null) return;

    for (const key of expandedKeys) {
      const keyStr = key as string;
      const depth = calculateFqnDepth(keyStr);
      if (depth < TREE_DEPTH.TAG && !loadedChildren.has(keyStr)) {
        setPendingLoadKey(keyStr);
        break;
      }
    }
  }, [expandedKeys, loadedChildren, pendingLoadKey]);

  const prevSelectedTagFqnRef = useRef<string | undefined>(selectedTagFqn);

  useEffect(() => {
    if (selectedTagFqn !== prevSelectedTagFqnRef.current) {
      prevSelectedTagFqnRef.current = selectedTagFqn;

      if (selectedTagFqn) {
        setSelectedKeys([selectedTagFqn]);
      } else if (tagCategory) {
        setSelectedKeys([tagCategory]);
      }
    }
  }, [selectedTagFqn, tagCategory]);

  useEffect(() => {
    if (!categoryData || categoryData.length === 0) return;

    if (selectedTagFqn) {
      setSelectedKeys([selectedTagFqn]);
      const parts = selectedTagFqn.split('.');
      const keysToExpand: string[] = [];
      for (let i = 1; i <= parts.length; i++) {
        const partialFqn = parts.slice(0, i).join('.');
        keysToExpand.push(partialFqn);
      }
      const filteredKeys = keysToExpand.filter((key) => calculateFqnDepth(key) < TREE_DEPTH.TAG);

      setExpandedKeys((prevKeys) => {
        const newKeysSet = new Set(prevKeys);
        filteredKeys.forEach((key) => newKeysSet.add(key));
        return Array.from(newKeysSet);
      });

      const parentsToLoad = filteredKeys.filter((key) => !loadedChildren.has(key));
      if (parentsToLoad.length > 0 && !pendingLoadKey) {
        setPendingLoadKey(parentsToLoad[0]);
      }

      setIsInitialized(true);
      return;
    }

    if (tagCategory && !isInitialized) {
      setSelectedKeys([tagCategory]);
      setExpandedKeys([tagCategory]);

      if (!loadedChildren.has(tagCategory)) {
        setPendingLoadKey(tagCategory);
      }

      setIsInitialized(true);
      return;
    }

    if (!isInitialized) {
      const firstCategory = categoryData[0];
      if (firstCategory) {
        const categoryName = firstCategory.name;
        setSelectedKeys([categoryName]);
        setExpandedKeys([categoryName]);

        if (!loadedChildren.has(categoryName)) {
          setPendingLoadKey(categoryName);
        }

        setIsInitialized(true);
        navigate(`${ROUTES.TAGS}/${categoryName}`);
      }
    }
  }, [selectedTagFqn, tagCategory, categoryData, isInitialized, loadedChildren, pendingLoadKey, navigate]);

  useEffect(() => {
    if (tagCategory) return;

    const firstCategory = treeData[0];
    if (firstCategory) {
      setExpandedKeys([firstCategory.key]);
      setSelectedKeys([firstCategory.key]);
      navigate(`${ROUTES.TAGS}/${firstCategory.key}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagCategory, treeData]);

  return (
    <CategoryTree
      headerTitle="태그 카테고리"
      isLoading={isCategoryLoading}
      showCount={true}
      treeData={treeData}
      loadingKeys={loadingKeys}
      expandedKeys={expandedKeys}
      selectedKeys={selectedKeys}
      onCollapseAll={onCollapseAll}
      onSelect={onSelect}
      onExpand={onExpand}
      onAddClick={onAddClick}
      addButtonText="분류 추가"
      showAddButton={canManageCategories}
    />
  );
};

type TagCategoryTreeErrorProps = React.ComponentProps<typeof ErrorFallback> & {
  title: string;
  description: string;
};

const Error = ({ title, description, ...props }: TagCategoryTreeErrorProps) => {
  return (
    <div
      style={{
        display: 'flex',
        height: '100%',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
      <ErrorFallback title={title} description={description} {...props} />
    </div>
  );
};

export const TagCategoryTree = { Tree, Error };
